import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  loadStatusColors,
  loadStatusTextMap,
} from '@rematter/modules/trade/utils/status';
import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedOutboundStatuses } from '../../../_state/transactions.state';

import { FilterOptionLabel } from './FilterOptionLabel';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface StatusOption {
  id: LoadStatus;
  label: string;
}

interface OutboundStatusColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ OUTBOUND STATUS COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const OutboundStatusColumnFilter: React.FC<
  OutboundStatusColumnFilterProps
> = ({ onClose }) => {
  const t = useTranslations();

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Atoms
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedOutboundStatuses,
  );
  const selectedStatuses = Array.from(selectedStatusesSet);

  // Memoized values
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(
    () =>
      Object.values(enumLoadStatus).map((status) => ({
        id: status,
        label: loadStatusTextMap[status](t, true),
      })),
    [t],
  );

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as LoadStatus[]));
    },
    [setSelectedStatusesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <FilterOptionLabel
        label={item.label}
        badge={
          <span
            className={cn('size-2 rounded-full', loadStatusColors[item.id])}
          />
        }
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@OutboundStatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.transactionsView.filters.status.searchPlaceholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
